import { ApolloQueryResult, DocumentNode, FetchResult, gql, OperationVariables, QueryHookOptions, QueryResult } from '@apollo/client';
import { ClientLogger } from '../client-logger';
import {
  personOrgQuery,
  personOrgCreateConnectPersonMutation,
  PersonOrgCreateConnectPersonInput,
  personOrgCreateConnectPersonMutationVariables,
  personOrgCreateNewPersonMutation,
  PersonOrgCreateNewPersonInput,
  personOrgCreateNewPersonMutationVariables,
  personOrgQueryVariables,
  personsQuery,
  personsQueryVariables,
  personOrgUpdateMutation,
  personOrgUpdateMutationVariables,
  personOrgVerificationsQuery,
  personOrgVerificationsQueryVariables,
  personOrgDeleteMutation,
  personDeleteMutation,
  PersonOrgUpdateInput,
  personOrgUpsertMutation,
  personOrgUpsertMutationVariables,
  PersonOrgUpsertInput,
  PersonOrgChangeRoleInput,
  personOrgChangeRoleMutation,
  personOrgChangeRoleMutationVariables,
  PersonOrgFragment,
  Person,
  PersonOrg,
} from './verid-api-types';
// import { persons_persons_edges_node, personOrg_personOrg, personOrgs_personOrgs_edge_node } from './api-types-parts';
import { useVeridApi } from './hybrid-apollo-client';
import { VERIFICATION_METHOD_FIELDS } from './use-verification-method-service';
import { ORG_FIELDS_CORE, PERSON_FIELDS, PERSON_ORG_FIELDS, PERSON_ORG_VERIFICATION_FIELDS } from './person-auth-gql';
import { useRef } from 'react';

const DEBUG = false;

// export type Person = Omit<
//   NonNullable<personsQuery['persons']['edges']>[number]['node'],
//   '__typename'
// >;
// export type PersonOrg = Omit<PersonOrgFragment, '__typename' | 'person'>;
// export type PersonOrgsNode = Omit<PersonOrgFragment, '__typename'>;

export function usePersonOrgService() {
  const api = useVeridApi();

  const personsQuery = gql`
    ${PERSON_ORG_FIELDS}
    ${PERSON_FIELDS}
    ${PERSON_ORG_VERIFICATION_FIELDS}
    ${ORG_FIELDS_CORE}
    query persons($orderBy: PersonOrder, $first: Int, $after: String, $query: PersonSearchInput!) {
      persons(query: $query, orderBy: $orderBy, first: $first, after: $after) {
        edges {
          cursor
          node {
            ...Person
            personOrgs {
              ...PersonOrg
              verificationsOf {
                ...PersonOrgVerification
              }
              org {
                ...OrgFieldsCore
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `;

  const apiFunctions = useRef({
    usePersonsQuery(options: QueryHookOptions<personsQuery, personsQueryVariables> | undefined) {
      function fct(): QueryResult<personsQuery, personsQueryVariables> {
        return api.useQuery(personsQuery, options);
      }
      return fct;
    },

    async personOrg(id: string): Promise<ApolloQueryResult<personOrgQuery>> {
      return api.query<personOrgQuery, personOrgQueryVariables>({
        query: gql`
          ${PERSON_ORG_FIELDS}
          ${PERSON_ORG_VERIFICATION_FIELDS}
          query personOrg($id: String!) {
            personOrg(id: $id) {
              ...PersonOrg
              verificationsOf {
                ...PersonOrgVerification
              }
            }
          }
        `,
        variables: { id },
        fetchPolicy: 'network-only',
      });
    },

    async personOrgVerifications(orgId: string, id: string): Promise<ApolloQueryResult<personOrgVerificationsQuery>> {
      return api.query<personOrgVerificationsQuery, personOrgVerificationsQueryVariables>({
        query: gql`
          ${PERSON_ORG_VERIFICATION_FIELDS}
          ${VERIFICATION_METHOD_FIELDS}
          query personOrgVerifications($orgId: String!, $id: String!) {
            personOrgVerifications(orgId: $orgId, id: $id) {
              ...PersonOrgVerification
              method {
                ...VerificationMethod
              }
            }
          }
        `,
        variables: { orgId, id },
        fetchPolicy: 'network-only',
      });
    },

    async personOrgCreateNewPerson(personOrg: PersonOrgCreateNewPersonInput): Promise<FetchResult<personOrgCreateNewPersonMutation>> {
      return api.mutate<personOrgCreateNewPersonMutation, personOrgCreateNewPersonMutationVariables>({
        mutation: gql`
          mutation personOrgCreateNewPerson($data: PersonOrgCreateNewPersonInput!) {
            personOrgCreateNewPerson(data: $data) {
              id
            }
          }
        `,
        variables: { data: personOrg },
        refetchQueries: ['persons'],
      });
    },
    async personOrgCreateConnectPerson(
      personOrg: PersonOrgCreateConnectPersonInput
    ): Promise<FetchResult<personOrgCreateConnectPersonMutation>> {
      return api.mutate<personOrgCreateConnectPersonMutation, personOrgCreateConnectPersonMutationVariables>({
        mutation: gql`
          mutation personOrgCreateConnectPerson($data: PersonOrgCreateConnectPersonInput!) {
            personOrgCreateConnectPerson(data: $data) {
              id
            }
          }
        `,
        variables: { data: personOrg },
        refetchQueries: ['persons'],
      });
    },

    async personOrgUpsert(data: PersonOrgUpsertInput): Promise<FetchResult<personOrgUpsertMutation>> {
      return api.mutate<personOrgUpsertMutation, personOrgUpsertMutationVariables>({
        mutation: gql`
          mutation personOrgUpsert($data: PersonOrgUpsertInput!) {
            personOrgUpsert(data: $data) {
              created
              personOrg {
                id
              }
              success
              updated
            }
          }
        `,
        variables: { data },
        refetchQueries: ['persons'],
      });
    },

    async personOrgChangeRole(data: PersonOrgChangeRoleInput): Promise<FetchResult<personOrgChangeRoleMutation>> {
      return api.mutate<personOrgChangeRoleMutation, personOrgChangeRoleMutationVariables>({
        mutation: gql`
          mutation personOrgChangeRole($data: PersonOrgChangeRoleInput!) {
            personOrgChangeRole(data: $data) {
              created
              personOrg {
                id
              }
              success
              updated
            }
          }
        `,
        variables: { data },
        refetchQueries: ['persons'],
      });
    },

    async personOrgUpdate(data: PersonOrgUpdateInput): Promise<FetchResult<personOrgUpdateMutation>> {
      return api.mutate<personOrgUpdateMutation, personOrgUpdateMutationVariables>({
        mutation: gql`
          mutation personOrgUpdate($data: PersonOrgUpdateInput!) {
            personOrgUpdate(data: $data) {
              id
            }
          }
        `,
        variables: { data },
        refetchQueries: ['persons'],
      });
    },

    async personOrgDelete(personOrgId: string): Promise<FetchResult<personOrgDeleteMutation>> {
      const deletedPersonOrg = await api.mutate<personOrgDeleteMutation>({
        mutation: gql`
          mutation personOrgDelete($id: String!) {
            personOrgDelete(id: $id) {
              id
            }
          }
        `,
        variables: { id: personOrgId },
        refetchQueries: ['persons'],
      });
      await api.refreshMe();
      return deletedPersonOrg;
    },

    async personDelete(personId: string): Promise<FetchResult<personDeleteMutation>> {
      const deletePerson = await api.mutate<personDeleteMutation>({
        mutation: gql`
          mutation personDelete($id: String!) {
            personDelete(id: $id) {
              id
            }
          }
        `,
        variables: { id: personId },
        refetchQueries: ['persons'],
      });
      await api.refreshMe();
      return deletePerson;
    },

    personOrgCalc(person: { personOrgs: { orgId: string }[] }, orgId: string | undefined): PersonOrg | undefined {
      if (!orgId) {
        return undefined;
      }
      const personOrg = person.personOrgs.find((p) => p.orgId === orgId);
      return personOrg as PersonOrg | undefined;
    },

    sysAccountsForOrg(orgId: string) {
      return api.query({
        query: gql`
          ${PERSON_ORG_FIELDS}
          ${ORG_FIELDS_CORE}
          ${PERSON_ORG_VERIFICATION_FIELDS}
          query personOrgs($orderBy: PersonOrgOrder, $first: Int, $after: String, $query: PersonOrgSearchInput!) {
            personOrgs(query: $query, orderBy: $orderBy, first: $first, after: $after) {
              edges {
                cursor
                node {
                  ...PersonOrg
                  verificationsOf {
                    ...PersonOrgVerification
                  }
                  org {
                    ...OrgFieldsCore
                  }
                }
              }
              pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                startCursor
              }
              totalCount
            }
          }
        `,
        variables: { query: { orgId: orgId } },
      });
    },
  });
  return apiFunctions.current;
}
